import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from "@mui/material";
import React from "react";

import { AnalysisEntity, AnalysisProfile } from "../../types";

interface ProfileMembersDialogProps {
  open: boolean;
  profile: AnalysisProfile | null;
  entities: AnalysisEntity[];
  onClose: () => void;
  onPeopleTabClick?: () => void;
}

export const ProfileMembersDialog: React.FC<ProfileMembersDialogProps> = ({
  open,
  profile,
  entities,
  onClose,
  onPeopleTabClick
}) => {
  if (!profile) {
    return null;
  }

  const members = entities
    .filter((e) => e.analysis_profile_id === profile.id)
    .sort((a, b) => {
      // Sort by status (active first) then by email
      if (a.status !== b.status) {
        return a.status === "active" ? -1 : 1;
      }
      return a.email.localeCompare(b.email);
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          pb: 2
        }}
      >
        {profile.name} - People
      </DialogTitle>
      <DialogContent>
        {members.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">
              No people assigned to this group
            </Typography>
          </Box>
        ) : (
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 2, mt: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {members.map((member, index) => (
                    <TableRow key={member.id} sx={{ height: 36 }}>
                      <TableCell sx={{
                        py: 0.5,
                        border: 0,
                        borderBottom: index === members.length - 1 ? 0 : 1,
                        borderColor: "divider"
                      }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Typography variant="body2">
                            {member.email}
                          </Typography>
                          {member.status === "disabled" && (
                            <Chip
                              label="Disabled"
                              size="small"
                              color="error"
                              sx={{ flexShrink: 0 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
        >
          To add or remove members from this group, use the{" "}
          <Typography
            component="span"
            variant="caption"
            color="primary"
            sx={{
              textDecoration: "underline",
              cursor: "pointer"
            }}
            onClick={() => {
              onPeopleTabClick?.();
              onClose();
            }}
          >
            People tab
          </Typography>
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
